import type { AsyncOperation } from '../api/active/generated/equipment';
import type { components, operations } from '../api/active/generated/openapi';
import type {
  OrderConfirmationAccepted,
  OrderConfirmationConfirmRequest,
  OrderConfirmationContext,
  OrderConfirmationDetail,
  OrderConfirmationRetryAccepted,
  OrderConfirmationSubmissionStatus,
} from '../api/active/generated/orderConfirmation';
import type {
  CreateEmptyQuoteMasterRequest,
  CreateQuoteDraftRequest,
  CustomerLinkReadResult,
  DeriveQuoteDraftRequest,
  DiscardQuoteDraftRequest,
  ExportJob,
  FixedQuoteShareLink,
  PreparedQuoteImage,
  PrepareQuoteImageRequest,
  PreviewQuoteVersionOutputRequest,
  PreviewQuoteVersionOutputResult,
  PublishCustomerShareRequest,
  PublishCustomerShareResult,
  QuoteDraftPage,
  QuoteManagementPage,
  QuoteMasterRecord,
  QuoteOutputOptions,
  QuoteVersionExportRequest,
  QuoteVersionExportResult,
  QuoteVersionMutationResult,
  QuoteVersionNode,
  QuoteVersionTree,
  QuoteWorkspaceAggregate,
  RefreshQuoteVersionMediaRequest,
  RefreshQuoteVersionMediaResult,
  SaveQuoteDraftRequest,
  ShareLinkLifecycleRequest,
} from '../api/active/generated/quote';
import type {
  QuoteFieldDefinitionList,
  QuoteFieldPoolList,
} from '../api/active/generated/settings';
import type { StoreMappingList } from '../api/active/generated/storeMapping';
import { randomUuid } from '../api/active/randomUuid';
import { startRequestBodyPerformance } from '../api/active/requestPerformance';
import type { ActiveTransport } from '../api/active/transport';
import type { components as FrozenComponents } from '../api/generated/types';
import { exportDownloadRequestUrl } from '../catalog-media/localExportDownload';
import { localSignedMediaUrl } from '../catalog-media/localSignedMedia';
import { QUOTE_SOURCE_FRAGMENT_VERSION } from '../domain/quoteSourceFragment';
import { startQuoteExportTiming } from '../shell/workbenchPerformance';
import { type ProjectDelivery, parseProjectDelivery } from './projectDelivery';
import {
  quoteDraftWrite,
  quoteLocalPackageDefinitionWrite,
} from './quoteLocalDefinitionWrite';

export type PreparedQuoteCanvasImage = PreparedQuoteImage;

export type QuoteApiFieldError = components['schemas']['FieldError'];

export class QuoteApiError extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | undefined,
    message: string,
    readonly currentResourceVersion?: string | null,
    readonly traceId?: string,
    readonly fieldErrors?: readonly QuoteApiFieldError[],
  ) {
    super(message);
  }
}

export class QuoteLinkLifecycleError extends Error {
  constructor(
    readonly stage: string,
    readonly submitted: boolean,
    cause: unknown,
  ) {
    const detail = cause instanceof Error ? cause.message : '请求失败';
    const diagnostics =
      cause instanceof QuoteApiError
        ? [
            cause.status ? `HTTP ${cause.status}` : '',
            cause.code,
            cause.traceId ? `trace ${cause.traceId}` : '',
          ]
            .filter(Boolean)
            .join(' / ')
        : '';
    super(
      `${submitted ? '链接命令提交已返回成功；' : ''}${stage}失败：${detail}${diagnostics ? `（${diagnostics}）` : ''}`,
    );
  }
}

type Versioned<T> = { value: T; etag: string; readStateVersion?: string };

export type QuoteMasterReadState =
  components['schemas']['QuoteMasterReadState'];

export type QuoteLocalPackageDefinitionDto =
  components['schemas']['QuoteLocalPackageDefinition'];
export type QuoteLocalPlanDefinitionDto =
  components['schemas']['QuoteLocalPlanDefinition'];
export type QuoteLocalPlanInstanceDto =
  components['schemas']['QuoteLocalPlanInstance'];
export type QuoteLocalDefinitionPayload =
  | QuoteLocalPackageDefinitionDto
  | QuoteLocalPlanDefinitionDto;
export type ReplaceQuoteLocalDefinitionsRequest = {
  expectedResourceVersion: string;
  localPackageDefinitions: QuoteLocalPackageDefinitionDto[];
  localPlanDefinitions: QuoteLocalPlanDefinitionDto[];
};
export type ReplaceQuoteLocalDefinitionsResponse = {
  quoteId: string;
  quoteVersionId: string;
  resourceVersion: string;
  localPackageDefinitions: QuoteLocalPackageDefinitionDto[];
  localPlanDefinitions: QuoteLocalPlanDefinitionDto[];
  replayed: boolean;
};

export type ResolveQuoteLocalPlanDefinitionResponse = {
  quoteId: string;
  quoteVersionId: string;
  quoteResourceVersion: string;
  definitionId: string;
  definitionResourceVersion: string;
  name: string;
  discountPercent: string;
  businessModeCode: string;
  dependencyFingerprint: string;
  members: Array<{
    occurrenceId: string;
    quantityPerSet: number;
    source:
      | {
          type: 'catalog-package';
          packageId: string;
          packageRevisionId: string;
        }
      | {
          type: 'local-package';
          definitionId: string;
          definitionResourceVersion: string;
        };
    equipmentMembers: Array<{
      deviceId: string;
      deviceRevisionId: string;
      quantityPerPackage: number;
      packageRelationId: string | null;
      packageItemPriceCents: number | null;
      rentalPriceCents: number | null;
      shortRentalPriceCents: number | null;
      taxInclusivePlanPriceCents: number | null;
      untaxedPlanPriceCents: number | null;
      retailPriceTax13Cents: number | null;
      dynamicCostCents: number | null;
      businessModeCode: string;
      priceContextVersion: string;
    }>;
  }>;
};

export type OrderConfirmationOcrCreateRequest = {
  quoteVersionId: string;
  resourceVersion: string;
  expectedFields?: Array<
    FrozenComponents['schemas']['OrderConfirmationOcrField']
  >;
  image: File;
};
export type OrderConfirmationOcrResult =
  FrozenComponents['schemas']['OrderConfirmationOcrResult'];
export type OrderConfirmationOcrCancelReason =
  FrozenComponents['schemas']['OrderConfirmationOcrCancelRequest']['reason'];
export type OrderConfirmationDeliveryStatus = Omit<
  OrderConfirmationSubmissionStatus,
  'projectDelivery'
> & { projectDelivery?: ProjectDelivery | null };

async function problem(response: Response): Promise<QuoteApiError> {
  let value: {
    code?: string;
    errorCode?: string;
    title?: string;
    detail?: string;
    currentResourceVersion?: string | null;
    traceId?: string;
    fieldErrors?: unknown;
  } = {};
  try {
    const body: unknown = await response.json();
    if (body && typeof body === 'object' && !Array.isArray(body))
      value = body as typeof value;
  } catch {
    // Keep the HTTP status when an upstream proxy has no Problem body.
  }
  return new QuoteApiError(
    response.status,
    value.code ?? value.errorCode,
    value.detail ?? value.title ?? `报价请求失败（HTTP ${response.status}）。`,
    value.currentResourceVersion,
    typeof value.traceId === 'string' &&
      /^[a-zA-Z0-9_-]{1,128}$/.test(value.traceId)
      ? value.traceId
      : undefined,
    Array.isArray(value.fieldErrors)
      ? value.fieldErrors.flatMap((field: unknown) => {
          if (!field || typeof field !== 'object') return [];
          const entry = field as Record<string, unknown>;
          return typeof entry.path === 'string' &&
            typeof entry.code === 'string' &&
            typeof entry.message === 'string'
            ? [{ path: entry.path, code: entry.code, message: entry.message }]
            : [];
        })
      : undefined,
  );
}

export type QuoteBusinessStatus = NonNullable<
  NonNullable<
    operations['listQuoteMasters']['parameters']['query']
  >['businessStatus']
>;

function queryString(query: {
  cursor?: string;
  limit?: number;
  search?: string;
  states?: readonly string[];
  businessStatus?: QuoteBusinessStatus;
  createdDateFrom?: string;
  createdDateTo?: string;
}) {
  const params = new URLSearchParams();
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.search?.trim()) params.set('search', query.search.trim());
  for (const state of query.states ?? []) params.append('states', state);
  if (query.businessStatus) params.set('businessStatus', query.businessStatus);
  if (query.createdDateFrom)
    params.set('createdDateFrom', query.createdDateFrom);
  if (query.createdDateTo) params.set('createdDateTo', query.createdDateTo);
  return params.toString();
}

async function read<T>(
  transport: ActiveTransport,
  path: string,
  init?: RequestInit,
): Promise<Versioned<T>> {
  const previous =
    init?.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : Array.isArray(init?.headers)
        ? Object.fromEntries(init.headers)
        : init?.headers;
  const response = await transport.requestResponse(path, {
    ...init,
    headers: {
      ...previous,
      'X-Quote-Content-Version': QUOTE_SOURCE_FRAGMENT_VERSION,
    },
  });
  if (!response.ok) throw await problem(response);
  const readStateVersion = response.headers.get('X-Quote-Read-Version');
  const finishBody = startRequestBodyPerformance(path, init?.method ?? 'GET');
  try {
    return {
      value: (await response.json()) as T,
      etag: response.headers.get('ETag') ?? '',
      ...(readStateVersion ? { readStateVersion } : {}),
    };
  } finally {
    finishBody();
  }
}

async function readUnversioned<T>(
  transport: ActiveTransport,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const previous =
    init?.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : Array.isArray(init?.headers)
        ? Object.fromEntries(init.headers)
        : init?.headers;
  const response = await transport.requestResponse(path, {
    ...init,
    headers: {
      ...previous,
      'X-Quote-Content-Version': QUOTE_SOURCE_FRAGMENT_VERSION,
    },
  });
  if (!response.ok) throw await problem(response);
  const finishBody = startRequestBodyPerformance(path, init?.method ?? 'GET');
  try {
    return (await response.json()) as T;
  } finally {
    finishBody();
  }
}

function commandHeaders(
  csrfToken: string | null,
  options: {
    ifMatch?: string;
    idempotencyKey?: string;
  } = {},
  hasJsonBody = true,
): Record<string, string> {
  return {
    ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
    'Idempotency-Key': options.idempotencyKey ?? randomUuid(),
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...(options.ifMatch ? { 'If-Match': options.ifMatch } : {}),
  };
}

export type QuoteMasterListQuery = {
  cursor?: string;
  limit?: number;
  search?: string;
  states?: readonly import('../api/active/generated/quote').QuoteManagementState[];
  businessStatus?: QuoteBusinessStatus;
  createdDateFrom?: string;
  createdDateTo?: string;
};

export const QUOTE_WORKSPACE_GROUP_KEYS = [
  'device_fulfillment',
  'base_price',
  'rental',
  'plan_package_price',
  'quote_result',
  'additional_info',
] as const;
export type QuoteWorkspaceGroupKey =
  (typeof QUOTE_WORKSPACE_GROUP_KEYS)[number];
export type QuoteWorkspaceGroupLabels = Record<QuoteWorkspaceGroupKey, string>;
export const DEFAULT_QUOTE_WORKSPACE_GROUP_LABELS: QuoteWorkspaceGroupLabels = {
  device_fulfillment: '设备与履约',
  base_price: '基础价格',
  rental: '租金/短租租金',
  plan_package_price: '方案与套餐价格',
  quote_result: '报价结果',
  additional_info: '补充信息',
};
/** Stable equipment definition field key, including future money fields. */
export type EquipmentPricePreferenceKey = string;
export type QuoteWorkspaceGroupSettings = {
  groupLabels: QuoteWorkspaceGroupLabels;
  supportedContentContractVersions?: string[];
  enabledAutoMatch?: boolean;
  resourceVersion: string;
};
export type QuoteWorkspaceUserPreferences = {
  selectedEquipmentPriceFields: EquipmentPricePreferenceKey[];
  resourceVersion: string;
};

const signedMediaRequestUrl = (url: string) =>
  localSignedMediaUrl(
    url,
    import.meta.env.DEV,
    import.meta.env.VITE_CPQ_DEV_SIGNED_MEDIA_ORIGIN,
    import.meta.env.VITE_CPQ_DEV_EXPORT_IDS,
  );

async function readSignedPreviewImage(
  source: string,
  signal?: AbortSignal,
): Promise<Blob> {
  if (new URL(source).protocol !== 'https:')
    throw new Error('报价图片地址无效');
  // 原显示图可能已缓存无 CORS 响应；按原签名 URL 重新取字节，不追加查询参数。
  const response = await fetch(signedMediaRequestUrl(source), {
    signal,
    credentials: 'omit',
    mode: 'cors',
    cache: 'reload',
  });
  if (!response.ok)
    throw new QuoteApiError(
      response.status,
      'QUOTE_IMAGE_READ_FAILED',
      `报价图片读取失败（HTTP ${response.status}）`,
    );
  const image = await response.blob();
  if (!image.type.startsWith('image/') || image.size === 0)
    throw new Error('报价图片内容无效');
  return image;
}

export function createQuoteApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  return {
    getQuoteWorkspaceContainerEditMode: (signal?: AbortSignal) =>
      readUnversioned<{
        enabled: boolean;
        resourceVersion: string;
      }>(transport, '/quote-workspace-container-edit-mode', {
        signal,
        cache: 'no-store',
      }),
    replaceQuoteWorkspaceContainerEditMode: (
      enabled: boolean,
      ifMatch: string,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      readUnversioned<{
        enabled: boolean;
        resourceVersion: string;
      }>(transport, '/quote-workspace-container-edit-mode', {
        method: 'PUT',
        headers: commandHeaders(csrfToken, { ...options, ifMatch }),
        body: JSON.stringify({ enabled }),
        signal: options.signal,
      }),
    readPreviewImage: readSignedPreviewImage,
    // 已授权导出返回的短时 URL 原样读取；不把会话 Cookie/CSRF 发送到 OSS。
    downloadExport: async (
      url: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const requestUrl = exportDownloadRequestUrl(
        url,
        import.meta.env.DEV,
        import.meta.env.VITE_CPQ_LOCAL_EXPORT_ORIGIN,
      );
      const timing = startQuoteExportTiming('quote.export.download');
      try {
        timing.step('download-headers');
        const response = await fetch(signedMediaRequestUrl(requestUrl), {
          signal,
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-store',
        });
        if (!response.ok)
          throw new QuoteApiError(
            response.status,
            'EXPORT_DOWNLOAD_FAILED',
            `导出文件下载失败（HTTP ${response.status}）`,
          );
        timing.step('download-body');
        const blob = await response.blob();
        if (blob.size === 0) throw new Error('导出文件为空，未开始下载');
        timing.finish('success');
        return blob;
      } catch (cause) {
        timing.finish('failed');
        throw cause;
      }
    },
    listQuoteMasters: (
      query: QuoteMasterListQuery = {},
      signal?: AbortSignal,
    ) =>
      readUnversioned<QuoteManagementPage>(
        transport,
        `/quote-masters?${queryString(query)}`,
        { signal, cache: 'no-store' },
      ),

    createQuoteMasterWithInitialDraft: (
      body: CreateEmptyQuoteMasterRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<
        import('../api/active/generated/quote').CreateEmptyQuoteMasterResult
      >(transport, '/quote-masters:initialize-empty', {
        method: 'POST',
        headers: commandHeaders(csrfToken, options),
        body: JSON.stringify(body),
        signal: options.signal,
      }),

    getQuoteMaster: (
      quoteId: string,
      signal?: AbortSignal,
      quoteVersionId?: string,
    ) =>
      read<QuoteWorkspaceAggregate>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}${quoteVersionId === undefined ? '' : `?${new URLSearchParams({ quoteVersionId })}`}`,
        { signal, cache: 'no-store' },
      ),

    getQuoteMasterReadState: (
      quoteId: string,
      signal?: AbortSignal,
      quoteVersionId?: string,
    ) =>
      read<QuoteMasterReadState>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/read-state${quoteVersionId === undefined ? '' : `?${new URLSearchParams({ quoteVersionId })}`}`,
        { signal, cache: 'no-store' },
      ),

    listQuoteFieldPool: (signal?: AbortSignal) =>
      readUnversioned<QuoteFieldPoolList>(transport, '/quote-field-pool', {
        signal,
        cache: 'no-store',
      }),

    listQuoteFieldDefinitions: (signal?: AbortSignal) =>
      readUnversioned<QuoteFieldDefinitionList>(transport, '/quote-fields', {
        signal,
        cache: 'no-store',
      }),

    archiveQuote: (
      quoteId: string,
      ifMatch: string,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      readUnversioned<QuoteMasterRecord>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}:archive`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }, false),
          signal: options.signal,
        },
      ),

    restoreQuote: (
      quoteId: string,
      ifMatch: string,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      readUnversioned<QuoteMasterRecord>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}:restore`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }, false),
          signal: options.signal,
        },
      ),

    getQuoteVersionTree: (quoteId: string, signal?: AbortSignal) =>
      readUnversioned<QuoteVersionTree>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions`,
        { signal, cache: 'no-store' },
      ),

    createQuoteDraft: (
      quoteId: string,
      ifMatch: string,
      body: CreateQuoteDraftRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteVersionMutationResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    listQuoteDrafts: (quoteId: string, signal?: AbortSignal) =>
      readUnversioned<QuoteDraftPage>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/drafts`,
        { signal, cache: 'no-store' },
      ),

    getCurrentPublishedQuoteVersion: (quoteId: string, signal?: AbortSignal) =>
      read<QuoteVersionNode>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/published-version`,
        { signal, cache: 'no-store' },
      ),

    copyQuoteMaster: (
      quoteId: string,
      quoteVersionId: string,
      ifMatch: string,
      body: operations['copyQuoteMaster']['requestBody']['content']['application/json'],
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteVersionMutationResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:copy`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    deriveQuoteDraft: (
      quoteId: string,
      quoteVersionId: string,
      ifMatch: string,
      body: DeriveQuoteDraftRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteVersionMutationResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:derive-draft`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    checkQuoteSources: (
      quoteId: string,
      quoteVersionId: string,
      body: SaveQuoteDraftRequest,
      options: { signal?: AbortSignal } = {},
    ) =>
      readUnversioned<{ sourcesCurrent: true }>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:check-sources`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    saveQuoteVersionDraft: (
      quoteId: string,
      quoteVersionId: string,
      ifMatch: string,
      body: SaveQuoteDraftRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteVersionMutationResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:save-draft`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(quoteDraftWrite(body)),
          signal: options.signal,
        },
      ),

    replaceQuoteLocalDefinitions: (
      quoteId: string,
      quoteVersionId: string,
      ifMatch: string,
      body: ReplaceQuoteLocalDefinitionsRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<ReplaceQuoteLocalDefinitionsResponse>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}/local-definitions`,
        {
          method: 'PUT',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify({
            ...body,
            localPackageDefinitions: body.localPackageDefinitions.map(
              quoteLocalPackageDefinitionWrite,
            ),
          }),
          signal: options.signal,
        },
      ),

    resolveQuoteLocalPlanDefinition: (
      quoteId: string,
      quoteVersionId: string,
      definitionId: string,
      definitionResourceVersion: string,
      businessModeCode: string,
      pricingView: 'rental' | 'purchase',
      signal?: AbortSignal,
    ) =>
      readUnversioned<ResolveQuoteLocalPlanDefinitionResponse>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}/local-plan-definitions/${encodeURIComponent(definitionId)}:resolve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({
            definitionResourceVersion,
            businessModeCode,
            pricingView,
          }),
          signal,
        },
      ),

    discardQuoteDraft: (
      quoteId: string,
      quoteVersionId: string,
      ifMatch: string,
      body: DiscardQuoteDraftRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteVersionMutationResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:discard-draft`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    getQuoteCustomerLink: (quoteId: string, signal?: AbortSignal) =>
      readUnversioned<CustomerLinkReadResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/customer-link`,
        { signal, cache: 'no-store' },
      ),

    publishQuoteCustomerShare: (
      quoteId: string,
      ifMatch: string,
      body: PublishCustomerShareRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<PublishCustomerShareResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/customer-link:publish`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(
            body.draftSave === undefined
              ? body
              : { ...body, draftSave: quoteDraftWrite(body.draftSave) },
          ),
          signal: options.signal,
        },
      ),

    changeQuoteShareLinkLifecycle: (
      quoteId: string,
      action: ShareLinkLifecycleRequest['action'],
      ifMatch: string,
      body: ShareLinkLifecycleRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      readUnversioned<FixedQuoteShareLink>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/share-link:${action}`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    getQuoteOutputOptions: (signal?: AbortSignal) =>
      readUnversioned<QuoteOutputOptions>(transport, '/quote-output-options', {
        signal,
        cache: 'no-store',
      }),

    getQuoteWorkspaceSettings: (signal?: AbortSignal) =>
      read<QuoteWorkspaceGroupSettings>(
        transport,
        '/quote-workspace-settings',
        { signal, cache: 'no-store' },
      ),

    replaceQuoteWorkspaceSettings: (
      ifMatch: string,
      groupLabels: QuoteWorkspaceGroupLabels,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteWorkspaceGroupSettings>(
        transport,
        '/quote-workspace-settings',
        {
          method: 'PUT',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify({ groupLabels }),
          signal: options.signal,
        },
      ),

    getQuoteWorkspacePreferences: (signal?: AbortSignal) =>
      read<QuoteWorkspaceUserPreferences>(
        transport,
        '/quote-workspace-preferences',
        { signal, cache: 'no-store' },
      ),

    replaceQuoteWorkspacePreferences: (
      ifMatch: string,
      selectedEquipmentPriceFields: EquipmentPricePreferenceKey[],
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<QuoteWorkspaceUserPreferences>(
        transport,
        '/quote-workspace-preferences',
        {
          method: 'PUT',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify({ selectedEquipmentPriceFields }),
          signal: options.signal,
        },
      ),

    previewQuoteVersionOutput: (
      quoteId: string,
      quoteVersionId: string,
      body: PreviewQuoteVersionOutputRequest,
      options: { signal?: AbortSignal } = {},
    ) =>
      readUnversioned<PreviewQuoteVersionOutputResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:preview-output`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, {}),
          body: JSON.stringify(body),
          signal: options.signal,
          cache: 'no-store',
        },
      ),

    refreshQuoteVersionMedia: (
      quoteId: string,
      quoteVersionId: string,
      body: RefreshQuoteVersionMediaRequest,
      options: { signal?: AbortSignal } = {},
    ) =>
      readUnversioned<RefreshQuoteVersionMediaResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:refresh-media`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, {}),
          body: JSON.stringify(body),
          signal: options.signal,
          cache: 'no-store',
        },
      ),

    prepareQuoteVersionImage: (
      quoteId: string,
      quoteVersionId: string,
      body: PrepareQuoteImageRequest,
      options: { signal?: AbortSignal } = {},
    ) =>
      readUnversioned<PreparedQuoteCanvasImage>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:prepare-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify(body),
          signal: options.signal,
          cache: 'no-store',
        },
      ),

    verifyQuoteVersionImage: async (
      quoteId: string,
      quoteVersionId: string,
      receipt: string,
      documentHash: string,
      signal?: AbortSignal,
    ) => {
      const response = await transport.requestResponse(
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:verify-image`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Quote-Content-Version': QUOTE_SOURCE_FRAGMENT_VERSION,
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify({ receipt, documentHash }),
          signal,
          cache: 'no-store',
        },
      );
      if (!response.ok) throw await problem(response);
    },

    createQuoteVersionExport: (
      quoteId: string,
      quoteVersionId: string,
      body: QuoteVersionExportRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      readUnversioned<QuoteVersionExportResult>(
        transport,
        `/quote-masters/${encodeURIComponent(quoteId)}/versions/${encodeURIComponent(quoteVersionId)}:export`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, options),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    getExportJob: (exportId: string, signal?: AbortSignal) =>
      readUnversioned<ExportJob>(
        transport,
        `/exports/${encodeURIComponent(exportId)}`,
        { signal, cache: 'no-store' },
      ),

    getOrderConfirmationContext: (quoteId: string, signal?: AbortSignal) =>
      read<OrderConfirmationContext>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/context`,
        { signal, cache: 'no-store' },
      ),

    getOrderConfirmationDetail: (
      quoteId: string,
      quoteVersionId: string,
      confirmationId: string,
      signal?: AbortSignal,
    ) =>
      read<OrderConfirmationDetail>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/detail?quoteVersionId=${encodeURIComponent(quoteVersionId)}&confirmationId=${encodeURIComponent(confirmationId)}`,
        { signal, cache: 'no-store' },
      ),

    getOrderConfirmationStatus: async (
      quoteId: string,
      quoteVersionId: string,
      signal?: AbortSignal,
    ): Promise<Versioned<OrderConfirmationDeliveryStatus>> => {
      const result = await read<OrderConfirmationDeliveryStatus>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/status?quoteVersionId=${encodeURIComponent(quoteVersionId)}`,
        { signal, cache: 'no-store' },
      );
      return {
        ...result,
        value: {
          ...result.value,
          projectDelivery: parseProjectDelivery(result.value.projectDelivery),
        },
      };
    },

    retryOrderConfirmation: (
      quoteId: string,
      quoteVersionId: string,
      confirmationVersion: string,
      options: { idempotencyKey: string; signal?: AbortSignal },
    ) =>
      read<OrderConfirmationRetryAccepted>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation:retry`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, {
            ...options,
            ifMatch: `"${confirmationVersion}"`,
          }),
          body: JSON.stringify({ quoteVersionId }),
          signal: options.signal,
        },
      ),

    createOrderConfirmationOcr: (
      quoteId: string,
      ifMatch: string,
      body: OrderConfirmationOcrCreateRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) => {
      const form = new FormData();
      form.append('quoteVersionId', body.quoteVersionId);
      form.append('resourceVersion', body.resourceVersion);
      for (const field of body.expectedFields ?? [])
        form.append('expectedFields', field);
      form.append('image', body.image, 'order-screenshot');
      const headers = commandHeaders(csrfToken, { ...options, ifMatch });
      delete headers['Content-Type'];
      return read<OrderConfirmationOcrResult>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/ocr`,
        {
          method: 'POST',
          headers,
          body: form,
          signal: options.signal,
        },
      );
    },

    getOrderConfirmationOcr: (
      quoteId: string,
      operationId: string,
      signal?: AbortSignal,
    ) =>
      read<OrderConfirmationOcrResult>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/ocr/${encodeURIComponent(operationId)}`,
        { signal, cache: 'no-store' },
      ),

    cancelOrderConfirmationOcr: (
      quoteId: string,
      operationId: string,
      ifMatch: string,
      reason: OrderConfirmationOcrCancelReason,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<OrderConfirmationOcrResult>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation/ocr/${encodeURIComponent(operationId)}`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify({ reason }),
          signal: options.signal,
        },
      ),

    listStoreMappings: (signal?: AbortSignal) =>
      readUnversioned<StoreMappingList>(transport, '/store-mappings', {
        signal,
        cache: 'no-store',
      }),

    confirmOrder: (
      quoteId: string,
      ifMatch: string,
      body: OrderConfirmationConfirmRequest,
      options: { idempotencyKey?: string; signal?: AbortSignal } = {},
    ) =>
      read<OrderConfirmationAccepted>(
        transport,
        `/quotes/${encodeURIComponent(quoteId)}/order-confirmation:confirm`,
        {
          method: 'POST',
          headers: commandHeaders(csrfToken, { ...options, ifMatch }),
          body: JSON.stringify(body),
          signal: options.signal,
        },
      ),

    getAsyncOperation: (operationId: string, signal?: AbortSignal) =>
      read<AsyncOperation>(
        transport,
        `/operations/${encodeURIComponent(operationId)}`,
        { signal, cache: 'no-store' },
      ),
  };
}

export type QuoteApi = ReturnType<typeof createQuoteApi>;
